import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  List,
  Popover,
  Row,
  Space,
  Tag,
  Typography,
  message,
  theme
} from "antd";
import { CheckOutlined, CloseOutlined, ToolOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import PageHeader from "../shared/PageHeader";
import { formatDate, formatPeriod } from "../shared/money";
import { errorText, openBlob } from "../api";
import * as api from "./api";

const SUGGESTIONS = [
  "Welche Rechnungen sind überfällig?",
  "Wie hoch ist die Umsatzsteuer-Zahllast für diesen Monat?",
  "Wie sieht die Liquidität in den nächsten 13 Wochen aus?",
  "Erkläre mir die GWG-Regel.",
  "Welche Fristen stehen an?"
];

const ActionCard = ({ action, onDone }) => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const [busy, setBusy] = useState(false);
  const run = async fn => {
    setBusy(true);
    try {
      await fn(action.id);
      onDone(action.id);
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card size="small" style={{ borderColor: token.colorWarning, marginTop: 8 }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Space wrap>
          <Tag color="gold">{t(`action_${action.type}`)}</Tag>
          <b className="acct-wrap">{action.label}</b>
        </Space>
        <Typography.Text type="secondary" className="acct-wrap" style={{ fontSize: 12 }}>
          {JSON.stringify(action.params)}
        </Typography.Text>
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            loading={busy}
            onClick={() => run(api.confirmAction)}
          >
            {t("Confirm")}
          </Button>
          <Button
            size="small"
            icon={<CloseOutlined />}
            disabled={busy}
            onClick={() => run(api.dismissAction)}
          >
            {t("Dismiss")}
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

const Message = ({ msg, onActionDone }) => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const own = msg.role === "user";
  return (
    <div
      style={{ display: "flex", justifyContent: own ? "flex-end" : "flex-start", marginBottom: 12 }}
    >
      <div
        className="acct-wrap"
        style={{
          maxWidth: "85%",
          background: own ? token.colorPrimaryBg : token.colorFillAlter,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          padding: "8px 12px"
        }}
      >
        {msg.trace?.length ? (
          <Space wrap style={{ marginBottom: 6 }}>
            {msg.trace.map((tr, i) => (
              // Click (touch) as well as hover opens the tool summary.
              <Popover
                key={i}
                trigger={["hover", "click"]}
                content={
                  <div style={{ maxWidth: 320, overflowWrap: "anywhere" }}>
                    {tr.summary || JSON.stringify(tr.input)}
                  </div>
                }
              >
                <Tag
                  icon={<ToolOutlined />}
                  color={tr.ok ? "blue" : "red"}
                  style={{ cursor: "pointer" }}
                >
                  {tr.tool}
                </Tag>
              </Popover>
            ))}
          </Space>
        ) : null}
        <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
          {msg.content}
        </Typography.Paragraph>
        {msg.actions
          ?.filter(a => a.status === "proposed")
          .map(a => (
            <ActionCard key={a.id} action={a} onDone={onActionDone} />
          ))}
        {msg.error ? <Typography.Text type="danger">{msg.error}</Typography.Text> : null}
        {!own && msg.model ? (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {msg.provider} · {msg.model}
          </Typography.Text>
        ) : null}
        {!own && msg.showHint ? (
          <div style={{ marginTop: 6 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("assistant_hint")}
            </Typography.Text>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const Chat = ({ status }) => {
  const { t, i18n } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async text => {
    const content = (text || input).trim();
    if (!content || busy) return;
    const history = [
      ...messages.filter(m => !m.error).map(m => ({ role: m.role, content: m.content })),
      { role: "user", content }
    ];
    setMessages(m => [...m, { role: "user", content }]);
    setInput("");
    setBusy(true);
    try {
      const res = await api.chat(history, i18n.language?.slice(0, 2));
      const r = res.data;
      setMessages(m => [
        ...m,
        {
          role: "assistant",
          content: r.reply,
          trace: r.trace,
          actions: r.actions,
          model: r.model,
          provider: r.provider,
          showHint: /absetz|darf ich|kann ich|soll ich|steuerlich/i.test(content)
        }
      ]);
    } catch (error) {
      setMessages(m => [...m, { role: "assistant", content: "", error: errorText(error) }]);
    } finally {
      setBusy(false);
    }
  };

  const actionDone = id =>
    setMessages(m =>
      m.map(x =>
        x.actions
          ? { ...x, actions: x.actions.map(a => (a.id === id ? { ...a, status: "done" } : a)) }
          : x
      )
    );

  const statusTag = status ? (
    <Tag
      color={status.configured ? "green" : "red"}
      style={{ marginInlineEnd: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}
    >
      {status.configured ? `${status.provider} · ${status.model}` : t("not configured")}
    </Tag>
  ) : null;

  // Phones: the page header already names the assistant, so the status moves into the body.
  return (
    <Card
      size="small"
      title={isMobile ? undefined : t("Accounting assistant")}
      extra={isMobile ? undefined : statusTag}
    >
      {isMobile && statusTag ? <div style={{ marginBottom: 8 }}>{statusTag}</div> : null}
      {status && !status.configured ? (
        <Alert
          type="warning"
          showIcon
          message={t("The assistant needs ANTHROPIC_API_KEY (or GEMINI_API_KEY) on the server.")}
          style={{ marginBottom: 8 }}
        />
      ) : null}
      <Alert type="info" showIcon style={{ marginBottom: 8 }} message={t("assistant_hint")} />
      <div style={{ minHeight: 320, maxHeight: "55vh", overflowY: "auto", padding: 8 }}>
        {messages.length === 0 ? (
          <Space wrap>
            {SUGGESTIONS.map(s => (
              <Button
                key={s}
                onClick={() => send(s)}
                style={{ whiteSpace: "normal", height: "auto", textAlign: "start" }}
              >
                {s}
              </Button>
            ))}
          </Space>
        ) : null}
        {messages.map((m, i) => (
          <Message key={i} msg={m} onActionDone={actionDone} />
        ))}
        {busy ? <Typography.Text type="secondary">{t("Thinking …")}</Typography.Text> : null}
        <div ref={bottom} />
      </div>
      <Space.Compact style={{ width: "100%", marginTop: 8 }}>
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 4 }}
          value={input}
          onChange={e => setInput(e.target.value)}
          onPressEnter={e => {
            if (!e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={t("Ask about your books …")}
          disabled={busy || (status && !status.configured)}
        />
        <Button
          type="primary"
          onClick={() => send()}
          loading={busy}
          disabled={status && !status.configured}
        >
          {t("Send")}
        </Button>
      </Space.Compact>
    </Card>
  );
};

const Letters = () => {
  const { t } = useTranslation("accounting");
  const [letters, setLetters] = useState([]);
  const [period, setPeriod] = useState(dayjs().subtract(1, "month"));
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api
      .listLetters()
      .then(res => setLetters(res.data.letters))
      .catch(error => message.error(errorText(error)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.generateLetter(period.format("YYYY-MM"), { force: true });
      message.success(res.data.mailed ? t("Letter generated and emailed") : t("Letter generated"));
      load();
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card size="small" title={t("Accountant letters")}>
      <div className="acct-actions" style={{ marginBottom: 8 }}>
        <DatePicker
          picker="month"
          value={period}
          onChange={v => v && setPeriod(v)}
          style={{ flex: "1 1 140px", maxWidth: 220 }}
        />
        <Button onClick={generate} loading={busy}>
          {t("Generate")}
        </Button>
      </div>
      <List
        size="small"
        dataSource={letters}
        locale={{
          emptyText: t(
            "No letters yet — one is written on the 3rd of each month for the previous month."
          )
        }}
        renderItem={l => (
          <List.Item
            actions={[
              <Button
                key="open"
                size="small"
                type="link"
                onClick={() =>
                  openBlob(api.letterBlob(l.id)).catch(e => message.error(errorText(e)))
                }
              >
                {t("Open")}
              </Button>,
              <Button
                key="dl"
                size="small"
                type="link"
                onClick={() =>
                  openBlob(api.letterBlob(l.id), l.name).catch(e => message.error(errorText(e)))
                }
              >
                {t("Download")}
              </Button>
            ]}
          >
            <List.Item.Meta
              title={
                <span className="acct-wrap">{`${t("Accountant letter")} ${formatPeriod(l.period)}`}</span>
              }
              description={`${formatDate(String(l.createdAt).slice(0, 10))}`}
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

const Assistant = () => {
  const { t } = useTranslation("accounting");
  const history = useHistory();
  const match = useRouteMatch("/user/accounting/assistant/:tab?");
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api
      .getStatus()
      .then(res => setStatus(res.data))
      .catch(() => setStatus({ configured: false }));
  }, []);
  const tab = match?.params?.tab;
  return (
    <div>
      <PageHeader
        title={t("Accounting assistant")}
        extra={
          <>
            <Button
              type={!tab ? "primary" : "default"}
              onClick={() => history.push("/user/accounting/assistant")}
            >
              {t("Chat")}
            </Button>
            <Button
              type={tab === "letters" ? "primary" : "default"}
              onClick={() => history.push("/user/accounting/assistant/letters")}
            >
              {t("Accountant letters")}
            </Button>
          </>
        }
      />
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={tab === "letters" ? 24 : 16}>
          {tab === "letters" ? <Letters /> : <Chat status={status} />}
        </Col>
        {tab !== "letters" ? (
          <Col xs={24} xl={8}>
            <Letters />
            <Card size="small" title={t("Explained rules")} style={{ marginTop: 16 }}>
              <Space wrap>
                {(status?.topics || []).map(topic => (
                  <Tag key={topic}>{topic}</Tag>
                ))}
              </Space>
              <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                {t(
                  "Ask e.g. “Erkläre § 13b” — the answer cites the paragraphs and ends with the tax advisor hint."
                )}
              </Typography.Paragraph>
            </Card>
          </Col>
        ) : null}
      </Row>
    </div>
  );
};

export default Assistant;
